import React from 'react';
import { Node, Edge } from '@xyflow/react';
import IconProvider from '../ui/IconProvider';
import { Button } from '../ui/button';
import { Input } from '../ui/InputDefault';
import { Label } from '../ui/label';
import { getCachedObjects } from '@/lib/stores/objectsStore';
import { getCachedDialogs } from '@/lib/stores/dialogStore';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';
import { useNavigate } from 'react-router-dom';
import { useTabContext } from '@/contexts/TabContext';

interface NodeDetailsPanelProps {
  selectedNode: Node | null;
  selectedEdge: Edge | null;
  onDeleteNode: (nodeId: string) => void;
  onDeleteEdge: (edgeId: string) => void;
  onUpdateEdgeLabel: (edgeId: string, label: string) => void;
  onClose: () => void;
}

export function NodeDetailsPanel({
  selectedNode,
  selectedEdge,
  onDeleteNode,
  onDeleteEdge,
  onUpdateEdgeLabel,
  onClose,
}: NodeDetailsPanelProps) {
  const { selectedGame } = useSimplifiedUserData();
  const navigate = useNavigate();
  const { openTab } = useTabContext();
  const [edgeLabel, setEdgeLabel] = React.useState('');

  React.useEffect(() => {
    if (selectedEdge?.data?.label) {
      setEdgeLabel(selectedEdge.data.label as string);
    } else {
      setEdgeLabel('');
    }
  }, [selectedEdge]);

  const handleUpdateLabel = () => {
    if (selectedEdge) {
      onUpdateEdgeLabel(selectedEdge.id, edgeLabel);
    }
  };

  const handleOpenItem = () => {
    if (!selectedNode || !selectedGame?.id) return;

    const itemType = selectedNode.data.itemType;
    const itemId = selectedNode.data.itemId;

    if (itemType === 'object') {
      const path = `/library/${itemId}`;
      openTab({
        id: path,
        title: selectedNode.data.label as string,
        path: path,
      });
      navigate(path);
    } else if (itemType === 'dialog') {
      const path = `/dialogs/${itemId}`;
      openTab({
        id: path,
        title: selectedNode.data.label as string,
        path: path,
      });
      navigate(path);
    }
  };

  // Get item details
  const getItemDetails = () => {
    if (!selectedNode || !selectedGame?.id) return null;

    const itemType = selectedNode.data.itemType;
    const itemId = selectedNode.data.itemId;

    if (itemType === 'object') {
      const objects = getCachedObjects(selectedGame.id);
      return objects.find(obj => obj.id === itemId);
    } else if (itemType === 'dialog') {
      const dialogs = getCachedDialogs(selectedGame.id);
      return dialogs.find(dlg => dlg.id === itemId);
    }

    return null;
  };

  const itemDetails = getItemDetails();

  if (!selectedNode && !selectedEdge) {
    return (
      <div className="w-80 border-l bg-card p-4 flex items-center justify-center text-muted-foreground">
        <div className="text-center">
          <IconProvider iconstring="fa-circle-info" size={32} className="mx-auto mb-2 opacity-50" />
          <p className="text-sm">Select a node or connection to view details</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-80 border-l bg-card overflow-y-auto">
      {/* Header */}
      <div className="sticky top-0 bg-card border-b p-4 flex items-center justify-between z-10">
        <h3 className="font-semibold">
          {selectedNode ? 'Node Details' : 'Connection Details'}
        </h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClose}
        >
          <IconProvider iconstring="fa-times" size={14} />
        </Button>
      </div>

      {/* Node Details */}
      {selectedNode && (
        <div className="p-4 space-y-4">
          <div>
            <Label className="text-xs text-muted-foreground">Type</Label>
            <div className="flex items-center gap-2 mt-1">
              <IconProvider 
                iconstring={selectedNode.data.itemType === 'object' ? 'fa-cube' : 'fa-comments'} 
                size={16} 
              />
              <span className="capitalize">{selectedNode.data.itemType as string}</span>
            </div>
          </div>

          <div>
            <Label className="text-xs text-muted-foreground">Name</Label>
            <div className="mt-1 font-medium">{selectedNode.data.label as string}</div>
          </div>

          {itemDetails && 'type' in itemDetails && itemDetails.type && (
            <div>
              <Label className="text-xs text-muted-foreground">Object Type</Label>
              <div className="mt-1">{itemDetails.type}</div>
            </div>
          )}

          {itemDetails && 'dialog_key' in itemDetails && itemDetails.dialog_key && (
            <div>
              <Label className="text-xs text-muted-foreground">Dialog Key</Label>
              <div className="mt-1 font-mono text-sm">{itemDetails.dialog_key}</div>
            </div>
          )}

          {itemDetails && 'tags' in itemDetails && itemDetails.tags && itemDetails.tags.length > 0 && (
            <div>
              <Label className="text-xs text-muted-foreground">Tags</Label>
              <div className="mt-1 flex flex-wrap gap-1">
                {itemDetails.tags.map((tag, idx) => (
                  <span 
                    key={idx}
                    className="px-2 py-1 text-xs rounded bg-secondary"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {itemDetails && 'status' in itemDetails && itemDetails.status && (
            <div>
              <Label className="text-xs text-muted-foreground">Status</Label>
              <div className="mt-1">{itemDetails.status}</div>
            </div>
          )}

          {itemDetails && 'description' in itemDetails && itemDetails.description && (
            <div>
              <Label className="text-xs text-muted-foreground">Description</Label>
              <div className="mt-1 text-sm text-muted-foreground">{itemDetails.description}</div>
            </div>
          )}

          <div className="pt-4 space-y-2">
            <Button
              onClick={handleOpenItem}
              className="w-full"
              variant="default"
            >
              <IconProvider iconstring="fa-arrow-up-right-from-square" size={14} className="mr-2" />
              Open Item
            </Button>
            <Button
              onClick={() => onDeleteNode(selectedNode.id)}
              className="w-full"
              variant="destructive"
            >
              <IconProvider iconstring="fa-trash" size={14} className="mr-2" />
              Remove from Canvas
            </Button>
          </div>
        </div>
      )}

      {/* Edge Details */}
      {selectedEdge && (
        <div className="p-4 space-y-4">
          <div>
            <Label className="text-xs text-muted-foreground">Connection Type</Label>
            <div className="flex items-center gap-2 mt-1 capitalize">
              <IconProvider iconstring="fa-arrow-right" size={16} />
              <span>{selectedEdge.data?.type as string || 'custom'}</span>
            </div>
          </div>

          <div>
            <Label htmlFor="edge-label">Label</Label>
            <div className="flex gap-2 mt-1">
              <Input
                id="edge-label"
                value={edgeLabel}
                onChange={(e) => setEdgeLabel(e.target.value)}
                placeholder="Enter label..."
              />
              <Button onClick={handleUpdateLabel} size="sm">
                <IconProvider iconstring="fa-check" size={14} />
              </Button>
            </div>
          </div>

          <div className="pt-4">
            <Button
              onClick={() => onDeleteEdge(selectedEdge.id)}
              className="w-full"
              variant="destructive"
            >
              <IconProvider iconstring="fa-trash" size={14} className="mr-2" />
              Delete Connection
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

